import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton, Chip, Tooltip,
  Drawer, List, ListItem, ListItemButton, ListItemText, Divider,
  Slide, useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RedeemIcon from '@mui/icons-material/Redeem';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// テスト公開中はポイント/ウォレット/ペルソナ系の機能を無効化する
//   .env (frontend) で REACT_APP_DISABLE_PAYMENTS=true を指定すると有効化
const PAYMENTS_DISABLED = process.env.REACT_APP_DISABLE_PAYMENTS === 'true';
const DISABLED_TOOLTIP = 'テスト公開中はご利用いただけません';

// 下スクロールで消える、上スクロールで表示される AppBar
function HideOnScroll({ children }) {
  const trigger = useScrollTrigger({ threshold: 40 });
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 2026-06-10 H-5 (user 報告: 「ドロワー が 閉じ ない」): 防御 2 重 化 —
  // route 遷移 で 必ず 閉じる (= 項目 click → navigate 後 に 開きっぱなし に なる 経路 を 遮断)
  const location = useLocation();
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  const [pointBalance, setPointBalance] = useState(null);

  // ポイント残高を取得
  useEffect(() => {
    if (!user) return;
    fetch('/api/points/balance', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPointBalance(data.pointBalance); })
      .catch(() => {});
  }, [user]);

  const handleLogout = () => { logout(); navigate('/'); setDrawerOpen(false); };

  const navLink = { fontSize: '14px', fontWeight: 500, color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'text.primary' } };

  // ===== 右側 account クラスタの chip 共通スタイル =====
  // height/border-radius/padding を統一して 「同じグループ」 の見え方を作る
  const chipBaseSx = {
    display: 'inline-flex',
    alignItems: 'center',
    height: 26,
    px: 1.1,
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '7px',
    lineHeight: 1,
    minHeight: 0,
    minWidth: 0,
    textTransform: 'none',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  };
  const chipIndigo = ({ disabled = false } = {}) => ({
    ...chipBaseSx,
    color: '#4f46e5',
    bgcolor: '#eef2ff',
    border: '1px solid #c7d2fe',
    textDecoration: 'none',
    '&:hover': { bgcolor: '#e0e7ff' },
    ...(disabled && {
      opacity: 0.5,
      '&.Mui-disabled': { color: '#4f46e5', bgcolor: '#eef2ff' },
    }),
  });
  const chipPink = ({ disabled = false } = {}) => ({
    ...chipBaseSx,
    color: '#be185d',
    bgcolor: '#fff1f2',
    border: '1px solid #fecdd3',
    textDecoration: 'none',
    '&:hover': { bgcolor: '#ffe4e6', borderColor: '#fda4af' },
    ...(disabled && {
      opacity: 0.5,
      '&.Mui-disabled': { color: '#be185d', bgcolor: '#fff1f2' },
    }),
  });

  const mobileNavItems = [
    { label: 'プロジェクト', to: '/projects', show: true },
    { label: 'ウォレット', to: '/wallet', show: !!user, disabledInTester: true },
    // オファーウォールはスマホブラウザ専用なのでモバイルドロワーのみに導線
    { label: '🎁 ポイントを貯める', to: '/offerwall', show: !!user, highlight: true, disabledInTester: true },
    { label: 'サブスク管理', to: '/subscription', show: !!user, disabledInTester: true },
    { label: 'ダッシュボード', to: '/dashboard', show: !!user },
  ];

  return (
    <>
      <HideOnScroll>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ maxWidth: 1080, width: '100%', mx: 'auto', px: { xs: 2, md: 3 }, minHeight: '56px !important' }}>
          {/* Penlytics ロゴ + wordmark
              ・mr: 5 を破壊しないよう margin shorthand 使用禁止
              ・hover bg は ::before 疑似要素で「レイアウト不変・視覚だけ拡張」 を実現
              ・ロゴの内側余白は image を 1.15 倍に拡大して PNG 内部の空白を相殺 */}
          <Box component={RouterLink} to="/"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 0.85,                                 // 7px (= ロゴ高 28 とのバランス: 28 ÷ 4 ≒ 7)
              textDecoration: 'none',
              mr: 5,                                     // ★ 維持: nav との間に 40px 確保
              py: 0.5,                                   // クリック領域だけ縦に広げる
              cursor: 'pointer',
              // 疑似要素で hover bg → レイアウトに一切影響しない
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '-2px -10px',                     // 上下2px 左右10px だけ広い hover 領域
                borderRadius: '12px',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                zIndex: 0,
                pointerEvents: 'none',
              },
              '&:hover::before': {
                backgroundColor: 'rgba(79, 70, 229, 0.07)',
                boxShadow: 'inset 0 0 0 1px rgba(79, 70, 229, 0.15)',
              },
              '& > *': { position: 'relative', zIndex: 1 },  // 子は ::before の上
              // ロゴタイル単体: hover で背景色を少し濃く (= 縁取り無しでも触ってる感)
              '&:hover .penlytics-logo-tile': {
                bgcolor: '#e0e7ff',                    // indigo-100 (= 通常 indigo-50 から一段濃く)
              },
            }}>
            {/* ロゴアイコン: 28px rounded square + 薄 indigo 背景 (= 縁取り廃止) */}
            <Box
              className="penlytics-logo-tile"
              sx={{
                width: 28, height: 28,
                borderRadius: '7px',
                bgcolor: '#eef2ff',
                padding: '1.5px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
              }}
            >
              <Box
                component="img"
                src="/icon-192.png"
                alt="Penlytics"
                loading="eager"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  // ★ PNG 自身に内部 padding (~13%) があるため、 1.15 倍に拡大して縁取りとの隙間を均す
                  transform: 'scale(1.15)',
                }}
              />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '17.5px',
                letterSpacing: '-0.5px',
                lineHeight: 1,
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Penlytics
            </Typography>
          </Box>

          {/* Desktop nav (= 純粋なナビゲーションのみ。 アクション系 CTA は右側 account クラスタへ) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2.5, flexGrow: 1, alignItems: 'center' }}>
            <Typography component={RouterLink} to="/projects" sx={navLink}>プロジェクト</Typography>
            {user && (
              PAYMENTS_DISABLED ? (
                <Tooltip title={DISABLED_TOOLTIP}>
                  <Typography sx={{ ...navLink, opacity: 0.4, cursor: 'not-allowed', textDecoration: 'line-through' }}>
                    ウォレット
                  </Typography>
                </Tooltip>
              ) : (
                <Typography component={RouterLink} to="/wallet" sx={navLink}>ウォレット</Typography>
              )
            )}
            {user && (
              PAYMENTS_DISABLED ? (
                <Tooltip title={DISABLED_TOOLTIP}>
                  <Typography sx={{ ...navLink, opacity: 0.4, cursor: 'not-allowed', textDecoration: 'line-through' }}>
                    サブスク管理
                  </Typography>
                </Tooltip>
              ) : (
                <Typography component={RouterLink} to="/subscription" sx={navLink}>サブスク管理</Typography>
              )
            )}
          </Box>

          {/* ===== Right account cluster =====
              Wallet/CTA chip 群 (= ポイント貯める + pt 残高 + あいぺん) を統一スタイル (height 26, radius 7px, font 12px) で並べ、
              区切りに divider を入れてから user 名 / logout の text button を続ける。
              これにより chip → text-button への素材切替が「区切られた別クラスター」 として読める。 */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75, ml: 'auto', alignItems: 'center' }}>
            {user ? (
              <>
                {/* ポイントを貯める CTA chip (= 旧: 左ナビの bold pink テキストから移動) */}
                {PAYMENTS_DISABLED ? (
                  <Tooltip title={DISABLED_TOOLTIP}>
                    <span>
                      <Button
                        disabled
                        size="small"
                        startIcon={<RedeemIcon sx={{ fontSize: '14px !important' }} />}
                        sx={chipPink({ disabled: true })}
                      >
                        貯める
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    component={RouterLink}
                    to="/offerwall"
                    size="small"
                    startIcon={<RedeemIcon sx={{ fontSize: '14px !important' }} />}
                    sx={chipPink()}
                  >
                    貯める
                  </Button>
                )}

                {/* pt 残高 chip */}
                {pointBalance !== null && (
                  PAYMENTS_DISABLED ? (
                    <Tooltip title={DISABLED_TOOLTIP}>
                      <span>
                        <Button
                          disabled
                          size="small"
                          startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '14px !important' }} />}
                          sx={chipIndigo({ disabled: true })}
                        >
                          {pointBalance.toLocaleString()} pt
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      component={RouterLink}
                      to="/wallet"
                      size="small"
                      startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '14px !important' }} />}
                      sx={chipIndigo()}
                    >
                      {pointBalance.toLocaleString()} pt
                    </Button>
                  )
                )}

                {/* あいぺん 連携 chip (= クリックでプロフィールを新タブで開く) */}
                {/* ★ ObjectId 形式の externalUserId のみ表示。 古い fallback 削除済み */}
                {Array.isArray(user.linkedAccounts) && (() => {
                  const isObjectId = (s) => typeof s === 'string' && /^[0-9a-fA-F]{24}$/.test(s);
                  const aipenLinks = user.linkedAccounts.filter(
                    a => a.provider === 'aipen' && isObjectId(a.externalUserId)
                  );
                  const valid = aipenLinks[aipenLinks.length - 1];
                  if (!valid) return null;
                  const aipenBase = (process.env.REACT_APP_AIPEN_URL || 'http://localhost').replace(/\/$/, '');
                  const userPageUrl = `${aipenBase}/user/${valid.externalUserId}`;
                  return (
                    <Tooltip title="あいぺんと連携済 ・ ユーザーページを開く (連携管理は Dashboard / Profile から)" arrow>
                      <Box
                        component="a"
                        href={userPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          ...chipBaseSx,
                          bgcolor: '#ecfdf5',
                          color: '#047857',
                          border: '1px solid #a7f3d0',
                          textDecoration: 'none',
                          '&:hover': { bgcolor: '#d1fae5', borderColor: '#6ee7b7' },
                        }}
                      >
                        <Box
                          component="img"
                          src={`${aipenBase}/logo192.png`}
                          alt="あいぺん"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          sx={{
                            width: 14, height: 14, mr: 0.5,
                            borderRadius: '3px',
                            objectFit: 'contain',
                          }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        あいぺん
                      </Box>
                    </Tooltip>
                  );
                })()}

                {/* Divider — chip クラスタと user/logout text-button クラスタを視覚的に分離 */}
                <Box
                  sx={{
                    width: '1px',
                    height: 18,
                    bgcolor: '#e2e8f0',
                    mx: 0.75,
                  }}
                />

                <Button
                  component={RouterLink}
                  to="/dashboard"
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '13px',
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1,
                    fontWeight: 500,
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  {user.displayName}
                </Button>
                <Button
                  onClick={handleLogout}
                  size="small"
                  sx={{
                    color: 'text.disabled',
                    fontSize: '13px',
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1,
                    '&:hover': { color: 'text.secondary', bgcolor: 'transparent' },
                  }}
                >
                  ログアウト
                </Button>
              </>
            ) : (
              // ログインは常に普通の見た目 (= 既存テスターはここから普通に入る)
              // public モード時に新規が押すと、 ログイン画面で「近日公開予定」 と案内
              <Button component={RouterLink} to="/login" variant="contained" size="small">
                ログイン
              </Button>
            )}
          </Box>

          {/* Mobile hamburger */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto' }}>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'text.primary' }}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      </HideOnScroll>

      {/* Mobile drawer */}
      {/* ★ 2026-05-08 改修: X ボタンの touch target を 44x44px 以上に拡大、
            背景クリックで閉じる動作も明示、 ListItem 余白とフォントを少し広めに */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{ sx: { width: { xs: '85vw', sm: 280 }, maxWidth: 320, borderRadius: '14px 0 0 14px' } }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1.25,
          minHeight: 56,
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', pl: 0.75 }}>メニュー</Typography>
          {/* ★ X ボタン: size 指定なし (= default 48x48 touch target) + 余白広め */}
          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label="メニューを閉じる"
            sx={{
              p: 1.25,
              color: 'text.primary',
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ py: 0.5 }}>
          {mobileNavItems.filter(n => n.show).map(item => {
            const isDisabled = PAYMENTS_DISABLED && item.disabledInTester;
            return (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={isDisabled ? 'div' : RouterLink}
                  to={isDisabled ? undefined : item.to}
                  disabled={isDisabled}
                  onClick={isDisabled ? undefined : () => setDrawerOpen(false)}
                  sx={{
                    py: 1.75,
                    px: 2.25,
                    minHeight: 52,
                    ...(item.highlight && !isDisabled && {
                      bgcolor: '#fef3f2',
                      '&:hover': { bgcolor: '#fee4e2' },
                    }),
                    ...(isDisabled && { opacity: 0.4, cursor: 'not-allowed' }),
                  }}
                >
                  <ListItemText
                    primary={isDisabled ? `${item.label} (準備中)` : item.label}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: item.highlight ? 700 : 500,
                      color: item.highlight && !isDisabled ? '#ff4f81' : 'inherit',
                      sx: isDisabled ? { textDecoration: 'line-through' } : undefined,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ p: 2 }}>
          {user ? (
            <>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1, wordBreak: 'break-all' }}>
                {user.displayName}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleLogout}
                sx={{ borderRadius: '10px', textTransform: 'none', py: 1.1, fontWeight: 600 }}
              >
                ログアウト
              </Button>
            </>
          ) : (
            <Button
              fullWidth
              variant="contained"
              component={RouterLink}
              to="/login"
              onClick={() => setDrawerOpen(false)}
              sx={{ borderRadius: '10px', textTransform: 'none', py: 1.1, fontWeight: 600 }}
            >
              ログイン
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
